import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ComponentProps } from 'react';
import { useEffect } from 'react';

import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';

import { isCardUsed } from 'lib/so5';

import { WarningDialog } from '../WarningDialog';
import type {
  LazyWarningDialogQuery,
  LazyWarningDialogQueryVariables,
} from './__generated__/index.graphql';

const LAZY_WARNING_DIALOG_QUERY = gql`
  query LazyWarningDialogQuery(
    $so5LeaderboardSlug: String!
    $cardsSlugs: [String!]
  ) {
    anyCards(slugs: $cardsSlugs) {
      slug
      ...isCardUsed_anyCard
      ...WarningDialog_anyCard
    }
  }
  ${WarningDialog.fragments.anyCard}
  ${isCardUsed.fragments.anyCard}
` as TypedDocumentNode<LazyWarningDialogQuery, LazyWarningDialogQueryVariables>;

type Props = {
  cardsSlugs: string[];
  so5LeaderboardSlug: string;
  so5LineupId: string;
} & Pick<
  ComponentProps<typeof WarningDialog>,
  'open' | 'onContinue' | 'onCancel'
>;

export const LazyWarningDialog = ({
  cardsSlugs,
  so5LeaderboardSlug,
  so5LineupId,
  onContinue,
  ...dialogProps
}: Props) => {
  const { data } = useQuery(LAZY_WARNING_DIALOG_QUERY, {
    variables: { cardsSlugs, so5LeaderboardSlug },
  });

  const { anyCards } = data || {};

  const someCardsAreUsed =
    anyCards?.length &&
    anyCards.some(anyCard => isCardUsed({ anyCard, so5LineupId }));

  useEffect(() => {
    if (anyCards?.length && !someCardsAreUsed) {
      onContinue();
    }
  }, [anyCards, onContinue, someCardsAreUsed]);

  if (anyCards?.length && someCardsAreUsed) {
    return (
      <WarningDialog
        cards={anyCards}
        so5LeaderboardSlug={so5LeaderboardSlug}
        so5LineupId={so5LineupId}
        onContinue={onContinue}
        {...dialogProps}
        droppedCards={[]}
      />
    );
  }

  return null;
};
